import React, { useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

interface RejectReasonModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (reason: string) => void;
	stationName: string;
	subtitle?: string | null;
	isSubmitting?: boolean;
}

const ModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	background-color: rgba(17, 24, 39, 0.55);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000000;
	padding: 20px;
`;

const ModalContainer = styled.div`
	width: 520px;
	max-width: 95vw;
	background: #ffffff;
	border-radius: 14px;
	box-shadow: 0 20px 40px rgba(15, 23, 42, 0.2);
	padding: 26px 28px 22px;
`;

const ModalTitle = styled.h3`
	margin: 0;
	font-size: 22px;
	font-weight: 700;
	color: #0f172a;
`;

const ModalSubtitle = styled.p`
	margin: 8px 0 0;
	font-size: 14px;
	color: #475569;
`;

const ModalMessage = styled.p`
	margin: 14px 0 0;
	font-size: 16px;
	line-height: 1.6;
	color: #334155;
`;

const InfoBox = styled.div`
	margin-top: 16px;
	padding: 12px 14px;
	border-radius: 12px;
	background: #fef2f2;
	border: 1px solid #fecaca;
	font-size: 13px;
	line-height: 1.55;
	color: #7f1d1d;
`;

const ReasonLabel = styled.label`
	display: block;
	margin-top: 18px;
	font-size: 14px;
	font-weight: 600;
	color: #334155;
	margin-bottom: 8px;
`;

const ReasonTextarea = styled.textarea`
	width: 100%;
	min-height: 108px;
	border: 1px solid #cbd5e1;
	border-radius: 10px;
	padding: 12px 14px;
	font-size: 14px;
	color: #0f172a;
	font-family: inherit;
	resize: vertical;
	box-sizing: border-box;

	&:focus {
		outline: none;
		border-color: #b91c1c;
		box-shadow: 0 0 0 3px rgba(185, 28, 28, 0.12);
	}
`;

const CharHint = styled.div`
	font-size: 12px;
	color: #94a3b8;
	margin-top: 4px;
	text-align: right;
`;

const Footer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	margin-top: 24px;
`;

const ActionButton = styled.button<{ variant: "cancel" | "confirm" }>`
	border-radius: 10px;
	border: none;
	padding: 11px 20px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	opacity: ${(props) => (props.disabled ? 0.7 : 1)};
	pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

	${(props) =>
		props.variant === "cancel"
			? `
		background: #e2e8f0;
		color: #334155;
		&:hover {
			background: #cbd5e1;
		}
	`
			: `
		background: #b91c1c;
		color: #ffffff;
		&:hover {
			background: #991b1b;
		}
	`}
`;

const MAX_REASON_LENGTH = 500;

export default function RejectReasonModal({
	isOpen,
	onClose,
	onConfirm,
	stationName,
	subtitle,
	isSubmitting = false,
}: RejectReasonModalProps) {
	const [reason, setReason] = useState("");

	if (!isOpen) return null;

	const trimmed = reason.trim();
	const canSubmit = trimmed.length > 0 && !isSubmitting;

	const handleOverlayClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget && !isSubmitting) {
			onClose();
		}
	};

	const handleClose = () => {
		if (!isSubmitting) {
			setReason("");
			onClose();
		}
	};

	const handleConfirm = () => {
		if (!canSubmit) return;
		onConfirm(trimmed);
	};

	const modalContent = (
		<ModalOverlay onClick={handleOverlayClick}>
			<ModalContainer onClick={(e) => e.stopPropagation()}>
				<ModalTitle>Reject payout request</ModalTitle>
				{subtitle ? <ModalSubtitle>{subtitle}</ModalSubtitle> : null}
				<ModalMessage>
					You are rejecting the payout request for <strong>{stationName}</strong>.
				</ModalMessage>
				<InfoBox>
					Rejecting this request will return the pending funds to the station's available balance, unlock any
					held bookings, and send the station an email with the reason you provide below.
				</InfoBox>
				<ReasonLabel htmlFor="reject-reason">
					Reason for rejection <span style={{ color: "#b91c1c" }}>*</span>
				</ReasonLabel>
				<ReasonTextarea
					id="reject-reason"
					value={reason}
					onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
						setReason(e.target.value.slice(0, MAX_REASON_LENGTH))
					}
					disabled={isSubmitting}
					placeholder="Enter the reason for rejecting this payout request..."
					maxLength={MAX_REASON_LENGTH}
					rows={4}
					aria-required="true"
					aria-label="Rejection reason"
				/>
				<CharHint>
					{reason.length} / {MAX_REASON_LENGTH}
				</CharHint>
				<Footer>
					<ActionButton
						variant="cancel"
						onClick={handleClose}
						disabled={isSubmitting}>
						Cancel
					</ActionButton>
					<ActionButton
						variant="confirm"
						onClick={handleConfirm}
						disabled={!canSubmit}>
						{isSubmitting ? "Rejecting..." : "Confirm rejection"}
					</ActionButton>
				</Footer>
			</ModalContainer>
		</ModalOverlay>
	);

	return createPortal(modalContent, document.body);
}
